import { PocketRow, PocketSection } from "@/components/ui";
import { WHATSAPP_COMMUNITY_URL } from "@/lib/community-url";
import { openExternalUrl } from "@/lib/open-external-url";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { settingsHelpMessages } from "@/i18n/messages/settings-help";
import type en from "@/i18n/messages/settings-help/en";

/**
 * Contact section for Settings.
 * Static link list with socials, email, and website — same
 * platforms and destinations as the landing footer.
 */

interface ContactLink {
  id: string;
  /** A brand or address shown as is, or a settings-help catalogue key. */
  label: { text: string } | { key: keyof typeof en };
  href: string;
  icon: React.ReactNode;
  testId: string;
}

const CONTACT_LINKS: ContactLink[] = [
  {
    id: "linkedin",
    label: { text: "LinkedIn" },
    href: "https://www.linkedin.com/company/denkerai/",
    icon: <Icons.Linkedin className="h-4 w-4" />,
    testId: "contact-link-linkedin",
  },
  {
    id: "x",
    label: { text: "X" },
    href: "https://x.com/Denker_AI",
    icon: <Icons.XLogo className="h-4 w-4" />,
    testId: "contact-link-x",
  },
  {
    id: "youtube",
    label: { text: "YouTube" },
    href: "https://www.youtube.com/@DenkerAI",
    icon: <Icons.Youtube className="h-4 w-4" />,
    testId: "contact-link-youtube",
  },
  {
    id: "tiktok",
    label: { text: "TikTok" },
    href: "https://www.tiktok.com/@denkerai",
    icon: <Icons.TikTok className="h-4 w-4" />,
    testId: "contact-link-tiktok",
  },
  {
    id: "whatsapp",
    label: { key: "whatsappCommunity" },
    href: WHATSAPP_COMMUNITY_URL,
    icon: <Icons.WhatsApp className="h-4 w-4" />,
    testId: "contact-link-whatsapp",
  },
  {
    id: "support-denker-ai",
    label: { text: "support@denker.ai" },
    href: "mailto:support@denker.ai",
    icon: <Icons.Mail className="h-4 w-4" />,
    testId: "contact-link-support-denker-ai",
  },
  {
    id: "denker-ai",
    label: { text: "denker.ai" },
    href: "https://www.denker.ai",
    icon: <Icons.Globe className="h-4 w-4" />,
    testId: "contact-link-denker-ai",
  },
];

export function ContactSection() {
  const t = useT(settingsHelpMessages);
  return (
    <PocketSection testId="settings-page-help-community">
      {CONTACT_LINKS.filter((link) => link.id !== "support-denker-ai").map(
        (link) => (
          <PocketRow
            key={link.id}
            testId={link.testId}
            title={"key" in link.label ? t(link.label.key) : link.label.text}
            leading={link.icon}
            trailing={
              <Icons.ExternalLink
                className="h-3 w-3 text-muted"
                aria-hidden="true"
              />
            }
            onSelect={() => void openExternalUrl(link.href)}
          />
        ),
      )}
    </PocketSection>
  );
}
